import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  Renderer2,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostListener,
  NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  CdkDragHandle,
  moveItemInArray
} from '@angular/cdk/drag-drop';


import { TotalBalanceWidget } from './widgets/total-balance/total-balance';
import { UpcomingBills } from './widgets/upcoming-bills/upcoming-bills';
import { SavingsGoal } from './widgets/savings-goal/savings-goal';
import { InsuranceOffers } from './widgets/insurance-offers/insurance-offers';
import { ExpenseTotalWidgetComponent } from './widgets/expense-total-widget/expense-total-widget';

import { AiAdviceWidgetComponent } from './widgets/ai-advice-widget/ai-advice-widget';
import { AiChatWidgetComponent } from './widgets/ai-chat-widget/ai-chat-widget';
import { LoanOffersWidgetComponent } from './widgets/loan-offers-widget/loan-offers-widget';
import { CashFlowPreviewWidgetComponent } from './widgets/cash-flow-preview-widget/cash-flow-preview-widget';
import { DebtsSummaryWidgetComponent } from './widgets/debts-summary-widget/debts-summary-widget';
import { ExpenseTracker } from './widgets/expense-tracker/expense-tracker';
import { AccountSummary } from './widgets/account-summary/account-summary';

import { MyCards } from './widgets/my-cards/my-cards';
import { DealsOffers } from './widgets/deals-offers/deals-offers';
import { CardOffersWidget } from './widgets/card-offers/card-offers';
import { BudgetStatus } from './widgets/budget-status/budget-status';
import { InvestmentOverviewWidgetComponent } from './widgets/investment-overview-widget/investment-overview-widget';
import { SpendingSnapshotWidgetComponent } from './widgets/spending-snapshot-widget/spending-snapshot-widget';
import { NetWorthTrendWidgetComponent } from './widgets/net-worth-trend-widget/net-worth-trend-widget';
import { WidgetsRightDrawer } from './widgets/widgets-right-drawer/widgets-right-drawer';
import { AddWidgetsModal } from '../../shared/components/add-widgets-modal/add-widgets-modal';
import { GuideModal } from '../../shared/components/guide-modal/guide-modal';
import { NotificationService } from '../../shared/services/notification.service';

import { TodaysExpenseTotalComponent } from './widgets/todays-expense-total/todays-expense-total';
import { LifetimeExpense } from './widgets/lifetime-expense/lifetime-expense';
import { EnergyExpense } from './widgets/energy-expense/energy-expense';
import { Loans } from './widgets/loans/loans';
import { InvestmentValue } from './widgets/investment-value/investment-value';


type WidgetSize = 'small' | 'medium' | 'half' | 'full';

interface Widget {
  id: string;
  title: string;
  type: string;
  size: WidgetSize;
  customHeight?: number;
  customWidthPx?: number;
  colSpan?: number;
}

interface SizeOption {
  value: WidgetSize;
  label: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, CdkDropList, CdkDrag, CdkDragHandle, WidgetsRightDrawer, AddWidgetsModal, GuideModal],
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Dashboard implements OnInit, OnDestroy, AfterViewInit {
  isEditMode = false;
  activeDropdownIndex: number | null = null;
  selectedWidgetIndex: number | null = null;
  showSuccessAlert = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';
  isWidgetsDrawerOpen = false;
  isAddWidgetsModalOpen = false;
  isGuideVisible = false;

  /**
   * Complete library of all available widgets and their default configurations.
   */
  private readonly WIDGET_LIBRARY: Widget[] = [
    { id: '1', title: 'Total Balance', type: 'balance', size: 'medium' },
    { id: '2', title: 'Upcoming Bills', type: 'bills', size: 'medium' },
    { id: '3', title: 'Savings Plans', type: 'savings', size: 'medium' },
    { id: '4', title: 'Budget Status', type: 'budget', size: 'medium' },
    { id: '5', title: 'Spending Snapshot', type: 'spending', size: 'medium' },
    { id: '6', title: 'Expense Total', type: 'expensetotal', size: 'medium' },
    { id: '7', title: 'AI Chat', type: 'aichat', size: 'medium' },
    { id: '8', title: 'AI Advice', type: 'aiadvice', size: 'medium' },
    { id: '9', title: 'Investment Overview', type: 'investmentoverview', size: 'medium' },
    { id: '10', title: 'Loan Offers', type: 'loanoffers', size: 'medium' },
    { id: '11', title: 'Insurance Offers', type: 'insurance', size: 'medium' },
    { id: '12', title: 'Cash Flow Preview', type: 'cashflowpreview', size: 'medium' },
    { id: '13', title: 'Debts Summary', type: 'debts', size: 'medium' },
    { id: '14', title: 'Expense Tracker', type: 'expensetracker', size: 'medium' },
    { id: '15', title: 'Accounts Summary', type: 'accountsummary', size: 'medium' },
    { id: '16', title: 'My Cards', type: 'mycards', size: 'medium' },
    { id: '17', title: 'Deals AND Offers', type: 'dealsoffers', size: 'medium' },
    { id: '18', title: 'Card Offers', type: 'cardoffers', size: 'medium' },
    { id: '19', title: 'Net Worth Trend', type: 'networthtrend', size: 'medium' },
    { id: '20', title: 'Today\'s Expense Total', type: 'todayexpensetotal', size: 'medium' },
    { id: '21', title: 'Lifetime Expense', type: 'lifetimeexpense', size: 'medium' },
    { id: '22', title: 'Energy Expense', type: 'energyexpense', size: 'medium' },
    { id: '23', title: 'Loans', type: 'loans', size: 'medium' },
    { id: '24', title: 'Investment Value', type: 'investmentvalue', size: 'medium' }
  ];


  /**
   * The set of widgets a user sees the very first time they ever open the app.
   */
  private readonly INITIAL_WIDGET_IDS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24'];


  widgets: Widget[] = [];
  private backupWidgets: Widget[] = [];

  private widgetSizeConstraints: Record<string, WidgetSize[]> = {
    balance: ['small', 'medium', 'half', 'full'],
    bills: ['small', 'medium', 'half', 'full'],
    savings: ['small', 'medium', 'half', 'full'],
    insurance: ['medium', 'half', 'full'],
    budget: ['small', 'medium', 'half', 'full'],
    spending: ['small', 'medium', 'half'],
    expensetotal: ['small', 'medium', 'half'],
    networthtrend: ['small', 'medium', 'half'],
    aiadvice: ['small', 'medium', 'half', 'full'],
    aichat: ['small', 'medium', 'half'],
    investmentoverview: ['small', 'medium', 'half', 'full'],
    loanoffers: ['medium', 'half', 'full'],
    cashflowpreview: ['small', 'medium', 'half', 'full'],
    debts: ['small', 'medium', 'half'],
    expensetracker: ['small', 'medium', 'half', 'full'],
    mycards: ['medium', 'half', 'full'],
    dealsoffers: ['medium', 'half', 'full'],
    cardoffers: ['medium', 'half', 'full'],
    accountsummary: ['small', 'medium', 'half', 'full'],
    todayexpensetotal: ['small', 'medium', 'half', 'full'],
    lifetimeexpense: ['small', 'medium', 'half', 'full'],
    energyexpense: ['small', 'medium', 'half', 'full'],
    loans: ['small', 'medium', 'half', 'full'],
    investmentvalue: ['small', 'medium', 'half', 'full']
  };


  private readonly RESIZE_CONFIGS: Record<string, { minHeight: number; minSpan: number; maxHeight?: number; maxSpan?: number }> = {
    insurance: { minHeight: 354, minSpan: 4, maxHeight: 400, maxSpan: 12 },
    loanoffers: { minHeight: 260, minSpan: 4, maxHeight: 480, maxSpan: 8 },
    mycards: { minHeight: 466, minSpan: 4, maxHeight: 500, maxSpan: 12 },
    dealsoffers: { minHeight: 466, minSpan: 4, maxHeight: 500, maxSpan: 12 },
    cardoffers: { minHeight: 296, minSpan: 4, maxHeight: 362, maxSpan: 12 },
    balance: { minHeight: 226, minSpan: 2, maxHeight: 480, maxSpan: 8 },
    accountsummary: { minHeight: 226, minSpan: 2, maxHeight: 288, maxSpan: 12 },
    expensetotal: { minHeight: 226, minSpan: 2, maxHeight: 370, maxSpan: 8 },
    spending: { minHeight: 226, minSpan: 2, maxHeight: 331, maxSpan: 8 },
    investmentoverview: { minHeight: 226, minSpan: 2, maxHeight: 450, maxSpan: 8 },
    cashflowpreview: { minHeight: 226, minSpan: 2, maxHeight: 288, maxSpan: 12 },
    budget: { minHeight: 226, minSpan: 2, maxHeight: 288, maxSpan: 12 },
    debts: { minHeight: 226, minSpan: 2, maxHeight: 260, maxSpan: 8 },
    todayexpensetotal: { minHeight: 226, minSpan: 2, maxHeight: 480, maxSpan: 12 },
    lifetimeexpense: { minHeight: 225, minSpan: 2, maxHeight: 450, maxSpan: 12 },
    energyexpense: { minHeight: 225, minSpan: 2, maxHeight: 450, maxSpan: 12 },
    aiadvice: { minHeight: 226, minSpan: 2, maxHeight: 450, maxSpan: 12 },
    loans: { minHeight: 225, minSpan: 2, maxHeight: 450, maxSpan: 12 },
    investmentvalue: { minHeight: 225, minSpan: 2, maxHeight: 450, maxSpan: 12 }
  };

  private readonly DEFAULT_RESIZE_CONFIG = { minHeight: 226, minSpan: 2, maxHeight: 480, maxSpan: 8 };

  private readonly SIZE_LABEL_MAP: Record<WidgetSize, string> = {
    small: 'Small Size',
    medium: 'Medium Size',
    half: 'Large Size',
    full: 'Extra Large'
  };

  private STORAGE_KEY = 'dashboard-widgets-v2';

  readonly componentMapper: Record<string, any> = {
    balance: TotalBalanceWidget,
    bills: UpcomingBills,
    savings: SavingsGoal,
    insurance: InsuranceOffers,
    spending: SpendingSnapshotWidgetComponent,
    expensetotal: ExpenseTotalWidgetComponent,
    networthtrend: NetWorthTrendWidgetComponent,
    aiadvice: AiAdviceWidgetComponent,
    aichat: AiChatWidgetComponent,
    investmentoverview: InvestmentOverviewWidgetComponent,
    loanoffers: LoanOffersWidgetComponent,
    cashflowpreview: CashFlowPreviewWidgetComponent,
    debts: DebtsSummaryWidgetComponent,
    expensetracker: ExpenseTracker,
    accountsummary: AccountSummary,
    mycards: MyCards,
    dealsoffers: DealsOffers,
    cardoffers: CardOffersWidget,
    budget: BudgetStatus,
    todayexpensetotal: TodaysExpenseTotalComponent,
    lifetimeexpense: LifetimeExpense,
    energyexpense: EnergyExpense,
    loans: Loans,
    investmentvalue: InvestmentValue
  };



  getAvailableSizes = (index: number): SizeOption[] => {
    const widget = this.widgets[index];
    const allowed = this.widgetSizeConstraints[widget.type] || ['small'];

    return allowed.map(size => ({
      value: size,
      label: this.SIZE_LABEL_MAP[size]
    }));
  };

  toggleSizeDropdown = (index: number, event: MouseEvent): void => {
    event.stopPropagation();
    if (!this.isEditMode) return;
    this.activeDropdownIndex = this.activeDropdownIndex === index ? null : index;
    this.cdr.detectChanges();
  };

  changeWidgetSize = (index: number, size: WidgetSize): void => {
    const widget = this.widgets[index];
    const allowed = this.widgetSizeConstraints[widget.type] || ['small'];

    if (allowed.includes(size)) {
      widget.size = size;
      widget.colSpan = undefined;
      widget.customHeight = undefined;
    }

    this.activeDropdownIndex = null;
    this.refreshWidgets();
  };

  removeWidget = (index: number): void => {
    this.widgets.splice(index, 1);
    this.activeDropdownIndex = null;
    this.refreshWidgets();
  };

  private clickOutsideHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (
      this.activeDropdownIndex !== null &&
      !target.closest('.pencil-wrapper') &&
      !target.closest('.size-dropdown')
    ) {
      this.activeDropdownIndex = null;
      this.cdr.detectChanges();
    }
  };

  private resizeObserver?: ResizeObserver;
  private resizeTimeout?: any;
  private isUpdating = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private ngZone: NgZone,
    private notificationService: NotificationService
  ) {
    document.addEventListener('click', this.clickOutsideHandler);
  }

  ngOnInit(): void {
    this.loadSavedLayout();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initMasonryLayout(), 200);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.clickOutsideHandler);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  refreshWidgets(): void {
    this.widgets = [...this.widgets];
    this.cdr.detectChanges();

    setTimeout(() => {
      this.reinitializeObservers();
      this.updateMasonryLayout();
    }, 50);

    setTimeout(() => this.updateMasonryLayout(), 150);
    setTimeout(() => this.updateMasonryLayout(), 300);
    setTimeout(() => this.updateMasonryLayout(), 500);
  }

  handleAddWidget(type: string): void {
    const exists = this.widgets.some(w => w.type === type);
    if (exists) {
      this.showToast('Widget is already available on dashboard', 'error');
    } else {
      const libraryWidget = this.WIDGET_LIBRARY.find(w => w.type === type);
      if (libraryWidget) {
        const newWidget = { ...libraryWidget, id: Date.now().toString() };
        this.widgets = [...this.widgets, newWidget];

        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.widgets));

        this.refreshWidgets();
        this.showToast('Widget added successfully', 'success');
      }
    }
  }

  private toastTimeout: any;

  showToast(message: string, type: 'success' | 'error'): void {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }

    this.toastMessage = message;
    this.toastType = type;
    this.showSuccessAlert = true;
    this.cdr.detectChanges();

    this.toastTimeout = setTimeout(() => {
      this.showSuccessAlert = false;
      this.toastTimeout = null;
      this.cdr.detectChanges();
    }, 3000);
  }

  private loadSavedLayout(): void {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        let parsed = JSON.parse(saved);

        this.widgets = parsed
          .filter(
            (w: any) =>
              w?.id &&
              w?.type &&
              this.componentMapper[w.type] &&
              ['small', 'medium', 'half', 'full'].includes(w.size)
          )
          .map((w: any) => ({
            ...w,
            size: this.widgetSizeConstraints[w.type]?.includes(w.size)
              ? w.size
              : this.widgetSizeConstraints[w.type][0]
          }));
      } else {
        this.widgets = this.WIDGET_LIBRARY.filter(w => this.INITIAL_WIDGET_IDS.includes(w.id));
      }
    } catch {
      this.widgets = this.WIDGET_LIBRARY.filter(w => this.INITIAL_WIDGET_IDS.includes(w.id));
    }

    this.refreshWidgets();
  }

  toggleEditMode(isCancel: boolean = true): void {
    if (this.isEditMode && isCancel && this.backupWidgets.length > 0) {
      this.widgets = JSON.parse(JSON.stringify(this.backupWidgets));
    }

    this.isEditMode = !this.isEditMode;

    if (this.isEditMode) {
      this.renderer.addClass(document.body, 'is-dashboard-edit-mode');
      this.backupWidgets = JSON.parse(JSON.stringify(this.widgets));
    } else {
      this.renderer.removeClass(document.body, 'is-dashboard-edit-mode');
      this.activeDropdownIndex = null;
      this.selectedWidgetIndex = null;
      this.backupWidgets = [];
    }

    this.refreshWidgets();
  }

  selectWidget(index: number): void {
    if (this.isEditMode) {
      this.selectedWidgetIndex = index;
      this.cdr.detectChanges();
    }
  }

  toggleWidgetsDrawer(): void {
    this.isWidgetsDrawerOpen = !this.isWidgetsDrawerOpen;
    this.cdr.detectChanges();
  }

  toggleAddWidgetsModal(): void {
    this.isAddWidgetsModalOpen = !this.isAddWidgetsModalOpen;
    this.cdr.detectChanges();
  }

  startIntro(): void {
    this.isGuideVisible = true;
    this.cdr.detectChanges();
  }

  closeGuide(): void {
    this.isGuideVisible = false;
    this.cdr.detectChanges();
  }

  toggleNotifications(): void {
    this.notificationService.toggle();
  }

  drop(event: CdkDragDrop<Widget[]>): void {
    if (!this.isEditMode) return;
    moveItemInArray(this.widgets, event.previousIndex, event.currentIndex);
    this.refreshWidgets();
  }

  saveLayout(showAlert: boolean = true): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.widgets));
    if (showAlert) {
      this.showToast('Dashboard layout saved successfully!', 'success');
    }
    this.toggleEditMode(false);
  }

  private initMasonryLayout(): void {
    this.updateMasonryLayout();

    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.updateMasonryLayout();
      }, 150);
    });

    const widgets = this.elementRef.nativeElement.querySelectorAll('.widget');
    widgets.forEach((widget: Element) => {
      this.resizeObserver?.observe(widget);
    });
  }

  private updateMasonryLayout(): void {
    if (this.isUpdating) return;
    this.isUpdating = true;

    requestAnimationFrame(() => {
      const widgetWrappers = this.elementRef.nativeElement.querySelectorAll('.widget-wrapper');
      const gap = 20;
      const rowHeight = 5;

      const measurements = Array.from(widgetWrappers).map((wrapper: any) => {
        const widget = wrapper.querySelector('.widget') as HTMLElement;
        return {
          wrapper,
          height: widget ? widget.offsetHeight : 0
        };
      });

      measurements.forEach(({ wrapper, height }) => {
        if (height === 0) return;
        const neededHeight = height + gap;
        const rowSpan = Math.ceil(neededHeight / rowHeight);
        const newSpan = `span ${rowSpan}`;

        if (wrapper.style.gridRowEnd !== newSpan) {
          this.renderer.setStyle(wrapper, 'grid-row-end', newSpan);
        }
      });

      this.isUpdating = false;
    });
  }

  private getSizeFromSpan(span: number): WidgetSize {
    if (span <= 3) return 'small';
    if (span <= 5) return 'medium';
    if (span <= 8) return 'half';
    return 'full';
  }

  private reinitializeObservers(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.updateMasonryLayout();
      }, 150);
    });

    const widgets = this.elementRef.nativeElement.querySelectorAll('.widget');
    widgets.forEach((widget: Element) => {
      this.resizeObserver?.observe(widget);
    });
  }

  private resizingWidget: Widget | null = null;
  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;
  private cachedGridWidth = 0;
  private cachedContainerPadding = 0;

  startResize(event: MouseEvent, widget: Widget): void {
    if (!this.isEditMode) return;
    event.preventDefault();
    event.stopPropagation();

    this.resizingWidget = widget;
    const target = event.target as HTMLElement;
    const widgetEl = target.closest('.widget') as HTMLElement;

    if (!widgetEl) return;

    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startWidth = widgetEl.getBoundingClientRect().width;
    this.startHeight = widgetEl.getBoundingClientRect().height;

    const container = this.elementRef.nativeElement.querySelector('.widget-container') as HTMLElement;
    if (container) {
      const style = window.getComputedStyle(container);
      this.cachedContainerPadding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      this.cachedGridWidth = container.offsetWidth - this.cachedContainerPadding;
    }

    if (!widget.customHeight) {
      widget.customHeight = this.startHeight;
    }

    document.body.classList.add('is-resizing');
    // Store which widget is being resized so other widgets can skip their updates
    document.body.dataset['resizingWidget'] = widget.type;

    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('mousemove', this.boundMouseMove);
      window.addEventListener('mouseup', this.boundMouseUp);
    });
  }

  private boundMouseMove = (e: MouseEvent) => this.onMouseMove(e);
  private boundMouseUp = () => this.onMouseUp();

  onMouseMove(event: MouseEvent): void {
    if (!this.resizingWidget) return;

    const widget = this.resizingWidget;
    const config = this.RESIZE_CONFIGS[widget.type] || this.DEFAULT_RESIZE_CONFIG;

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;

    let newHeight = Math.max(config.minHeight, this.startHeight + deltaY);
    if (config.maxHeight) {
      newHeight = Math.min(config.maxHeight, newHeight);
    }
    const currentPixelWidth = Math.max(100, this.startWidth + deltaX);

    const newWidthPx = currentPixelWidth;

    const widthPercentage = currentPixelWidth / this.cachedGridWidth;
    let newSpan = Math.max(config.minSpan, Math.round(widthPercentage * 12));
    const maxSpan = config.maxSpan || 12;
    newSpan = Math.min(maxSpan, newSpan);

    let hasChanged = false;

    if (widget.customHeight !== newHeight) {
      widget.customHeight = newHeight;
      hasChanged = true;
    }

    if (widget.customWidthPx !== newWidthPx) {
      widget.customWidthPx = newWidthPx;
      hasChanged = true;
    }

    if (widget.colSpan !== newSpan) {
      widget.colSpan = newSpan;
      widget.size = this.getSizeFromSpan(newSpan);
      hasChanged = true;
    }

    if (hasChanged) {
      this.ngZone.run(() => {
        this.updateMasonryLayout();
        this.cdr.detectChanges();
      });
    }
  }

  onMouseUp(): void {
    window.removeEventListener('mousemove', this.boundMouseMove);
    window.removeEventListener('mouseup', this.boundMouseUp);

    if (this.resizingWidget) {
      this.ngZone.run(() => {
        this.resizingWidget = null;
        document.body.classList.remove('is-resizing');
        delete document.body.dataset['resizingWidget'];
        this.refreshWidgets();
      });
    }
  }
}
